import React from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { apiFetch } from "@/lib/api";
import { PageLoader } from "@/components/common";
import { ShieldCheck, Star } from "lucide-react";

const STATS = [
  { k: "total_users", label: "Users" },
  { k: "total_sellers", label: "Sellers" },
  { k: "active_listings", label: "Active listings" },
  { k: "sold_listings", label: "Sold" },
  { k: "total_offers", label: "Offers" },
  { k: "pending_offers", label: "Pending offers" },
  { k: "pending_verifications", label: "Pending KYC" },
  { k: "open_reports", label: "Open reports" },
  { k: "banned_users", label: "Banned" },
];

export function AdminStats() {
  const { data: analytics, isLoading } = useQuery({ queryKey: ["admin-analytics"], queryFn: () => apiFetch("/admin/analytics") });
  const { data: daily } = useQuery({ queryKey: ["admin-daily"], queryFn: () => apiFetch("/admin/analytics/daily") });
  const { data: rankings } = useQuery({ queryKey: ["admin-rankings"], queryFn: () => apiFetch("/admin/rankings") });

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {STATS.map((s) => (
          <div key={s.k} className="rounded-2xl border border-border p-4" data-testid={`stat-${s.k}`}>
            <p className="text-2xl font-extrabold text-brand">{analytics[s.k] ?? 0}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {daily?.days && (
        <div className="rounded-2xl border border-border p-4">
          <h3 className="mb-3 font-semibold">Last 14 days</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={daily.days}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="signups" stroke="#2B2D31" strokeWidth={2} name="Signups" dot={false} />
                <Line type="monotone" dataKey="sales" stroke="#3F7D14" strokeWidth={2} name="Sales" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border p-4">
          <h3 className="mb-3 font-semibold">Top sellers</h3>
          <div className="space-y-2">
            {rankings?.top_sellers?.length ? rankings.top_sellers.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1">{i + 1}. {s.name}{s.verified && <ShieldCheck className="h-3.5 w-3.5 text-sky-600" />}</span>
                <span className="text-muted-foreground">{s.sold} sold · <Star className="inline h-3 w-3 text-amber-400" /> {s.rating_avg}</span>
              </div>
            )) : <p className="text-sm text-muted-foreground">No data yet.</p>}
          </div>
        </div>
        <div className="rounded-2xl border border-border p-4">
          <h3 className="mb-3 font-semibold">Top buyers</h3>
          <div className="space-y-2">
            {rankings?.top_buyers?.length ? rankings.top_buyers.map((b, i) => (
              <div key={b.id} className="flex items-center justify-between text-sm">
                <span>{i + 1}. {b.name}</span>
                <span className="text-muted-foreground">{b.offers} offers · {b.saves} saves</span>
              </div>
            )) : <p className="text-sm text-muted-foreground">No data yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
